// Pure view-model helpers for the Work BOARD surface (brief §5.2). Column order derives EXACTLY from
// the projection's lifecycle declaration; an unknown state key (carried by a count or an item) is
// appended LAST, never dropped (G-182 — the surface authors no column). The v1 filters (state /
// needs-a-decision / tag / subject search) narrow the rendered slice CLIENT-SIDE while the per-column
// counts stay whole-board `countsByState` (brief §5.2). No fetching, no JSX — pure functions only.

import type { WorkBoardProjection, WorkItemRow, WorkView } from '../projections.ts';

/** The four axes this surface renders — one per WorkView. A board only OFFERS the subset its
 *  projection declares in `availableViews`; the switcher renders exactly that set (never a dead tab). */
export type BoardAxis = 'kanban' | 'list' | 'calendar' | 'timeline';

/** The axis a definition opens on — the matching axis for its declared default view. */
export function defaultAxis(view: WorkView): BoardAxis {
  return view;
}

/** The axis to actually render: the override if the board offers it, else the default if offered,
 *  else kanban (always available). An axis outside `availableViews` never renders — no dead tab. */
export function resolveAxis(board: WorkBoardProjection, override: BoardAxis | null): BoardAxis {
  const offered = new Set<WorkView>(board.availableViews);
  if (override !== null && offered.has(override)) return override;
  const preferred = defaultAxis(board.view);
  if (offered.has(preferred)) return preferred;
  return 'kanban';
}

/** The v1 filter view-state (brief §5.2). Empty values mean "the whole slice". */
export interface BoardFilterState {
  readonly subjectSearch: string;
  readonly state: string | null;
  readonly tag: string | null;
  readonly needsDecision: boolean;
}

export const EMPTY_BOARD_FILTER: BoardFilterState = {
  subjectSearch: '',
  state: null,
  tag: null,
  needsDecision: false,
};

/** Working-column order: the lifecycle declaration, then any unknown state key last (never dropped).
 *  Exit states never become working columns — they render as the two exit columns. */
export function orderedBoardStates(board: WorkBoardProjection): string[] {
  const exitStates = new Set(board.exits.map((exit) => exit.state));
  const known = [...board.lifecycle];
  const seen = new Set(known);
  const extras: string[] = [];
  const note = (state: string): void => {
    if (seen.has(state) || exitStates.has(state)) return;
    seen.add(state);
    extras.push(state);
  };
  for (const key of Object.keys(board.countsByState)) note(key);
  for (const item of board.items) note(item.state);
  return [...known, ...extras];
}

/** Whether one row survives the v1 filters. Search is a case-blind substring over the subject display. */
export function matchesBoardFilter(item: WorkItemRow, filter: BoardFilterState): boolean {
  if (filter.state !== null && item.state !== filter.state) return false;
  if (filter.needsDecision && !item.needsDecision) return false;
  if (filter.tag !== null && !item.tags.includes(filter.tag)) return false;
  const query = filter.subjectSearch.trim().toLowerCase();
  if (query !== '' && !item.subjectDisplay.toLowerCase().includes(query)) return false;
  return true;
}

/** Narrow the item slice client-side (brief §5.2 — counts stay whole-board, the slice narrows). */
export function applyBoardFilter(
  items: readonly WorkItemRow[],
  filter: BoardFilterState,
): WorkItemRow[] {
  return items.filter((item) => matchesBoardFilter(item, filter));
}

/** Group the (filtered) slice by state for the kanban columns. */
export function groupItemsByState(items: readonly WorkItemRow[]): Map<string, WorkItemRow[]> {
  const byState = new Map<string, WorkItemRow[]>();
  for (const item of items) {
    const bucket = byState.get(item.state) ?? [];
    bucket.push(item);
    byState.set(item.state, bucket);
  }
  return byState;
}

/** The whole-board open-item count — the sum of `countsByState`, never the filtered slice. */
export function openItemsCount(counts: Readonly<Record<string, number>>): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}

/** The tag filter's options — every tag the slice carries, once, sorted. */
export function boardTagOptions(items: readonly WorkItemRow[]): string[] {
  return [...new Set(items.flatMap((item) => item.tags))].sort((a, b) => a.localeCompare(b));
}

/** Display form of a raw lifecycle key (`written_off` → `written off`) — presentation only. */
export function stateLabel(state: string): string {
  return state.replace(/_/g, ' ');
}

/** The exited-this-week stat's note, derived from the projection's exit states (G-182). */
export function exitNote(board: WorkBoardProjection): string {
  const labels = board.exits.map((exit) => stateLabel(exit.state));
  return labels.length > 0 ? labels.join(' or ') : 'left the board';
}
